import {
  ApiError as ApiErrorEnvelope,
  DownloadLink,
  dataResponse,
  type ErrorCode,
} from '@stayafrica/contracts';
import type { z } from 'zod';

/** Enveloppe `{ data: DownloadLink }` des routes de téléchargement. */
const DownloadLinkEnvelope = dataResponse(DownloadLink);

/** Base de l'API : `/api/v1` en développement (proxy Vite → http://localhost:3000). */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Nom du cookie CSRF non httpOnly (double-submit, cf. BRIEF §Conventions). */
const CSRF_COOKIE = 'sa_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/** Routes exemptées de CSRF côté serveur (login, mots de passe, invitations). */
const CSRF_EXEMPT = [/^\/auth\/login$/, /^\/auth\/password\//, /^\/auth\/invitations\/accept$/];

/**
 * Erreur d'API typée : `code` stable (catalogue `ErrorCode` des contrats),
 * message localisé par le serveur, `traceId` affichable par l'utilisateur.
 */
export class ApiRequestError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR' | 'MALFORMED_RESPONSE';
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly traceId: string | undefined;

  constructor(params: {
    code: ErrorCode | 'NETWORK_ERROR' | 'MALFORMED_RESPONSE';
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
    traceId?: string | undefined;
  }) {
    super(params.message);
    this.name = 'ApiRequestError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details;
    this.traceId = params.traceId;
  }

  /** L'utilisateur n'est pas (ou plus) authentifié : retour à la connexion. */
  get isUnauthenticated(): boolean {
    return (
      this.status === 401 && (this.code === 'UNAUTHENTICATED' || this.code === 'SESSION_EXPIRED')
    );
  }

  get isForbidden(): boolean {
    return this.code === 'PERMISSION_DENIED' || this.code === 'CSRF_TOKEN_INVALID';
  }

  get isNotFound(): boolean {
    return this.code === 'NOT_FOUND';
  }

  /** Erreurs de validation : `details` porte les champs en défaut. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'VALIDATION_ERROR' || !this.details) return {};
    const raw = this.details['fields'] ?? this.details;
    if (typeof raw !== 'object' || raw === null) return {};
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === 'string') result[key] = value;
      else if (Array.isArray(value) && typeof value[0] === 'string') result[key] = value[0];
    }
    return result;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Jeton CSRF renvoyé par `POST /auth/login` : conservé en mémoire, car le
 * cookie `sa_csrf` peut être indisponible (tests, navigation privée stricte).
 */
let csrfTokenFromLogin: string | undefined;
export function setCsrfToken(token: string | undefined): void {
  csrfTokenFromLogin = token;
}
export function getCsrfToken(): string | undefined {
  return readCookie(CSRF_COOKIE) ?? csrfTokenFromLogin;
}

/** Rappel invoqué sur 401 : l'application redirige vers /login avec retour. */
type UnauthenticatedHandler = (error: ApiRequestError) => void;
let onUnauthenticated: UnauthenticatedHandler | undefined;
export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | undefined): void {
  onUnauthenticated = handler;
}

export interface RequestOptions<TSchema extends z.ZodTypeAny | undefined> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Corps JSON ; sérialisé automatiquement. */
  body?: unknown;
  /** Paramètres de requête ; les valeurs `undefined` sont ignorées. */
  query?: Record<string, string | number | boolean | undefined>;
  /** Schéma Zod de la réponse : la réponse est validée avant d'atteindre l'UI. */
  schema?: TSchema;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** N'invoque pas le gestionnaire global de 401 (utilisé par `/auth/me`). */
  skipAuthRedirect?: boolean;
}

type Parsed<TSchema> = TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown;

/**
 * Traduit une réponse HTTP en erreur typée depuis l'enveloppe
 * `{ error: { code, message, details, traceId } }` de l'API, et déclenche la
 * redirection de connexion sur un 401.
 */
function raiseApiError(
  response: Response,
  payload: unknown,
  traceId: string | undefined,
  skipAuthRedirect?: boolean,
): ApiRequestError {
  const envelope = ApiErrorEnvelope.safeParse(payload);
  const error = envelope.success
    ? new ApiRequestError({
        code: envelope.data.error.code,
        message: envelope.data.error.message,
        status: response.status,
        details: envelope.data.error.details,
        traceId: envelope.data.error.traceId ?? traceId,
      })
    : new ApiRequestError({
        code: 'INTERNAL_ERROR',
        message: 'Une erreur inattendue est survenue.',
        status: response.status,
        traceId,
      });
  if (error.isUnauthenticated && !skipAuthRedirect) onUnauthenticated?.(error);
  return error;
}

function buildUrl(path: string, query?: RequestOptions<undefined>['query']): string {
  // URL absolue : Node (tests, SSR) n'accepte pas les chemins relatifs dans fetch.
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost';
  const url = new URL(`${API_BASE_URL}${path}`, origin).toString();
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Client HTTP unique de l'application.
 * - cookie de session envoyé (`credentials: 'include'`) ;
 * - en-tête CSRF sur les méthodes non idempotentes ;
 * - enveloppe d'erreur `{ error: { code, message, details, traceId } }` typée ;
 * - réponse validée par le schéma Zod des contrats partagés.
 */
export async function apiRequest<TSchema extends z.ZodTypeAny | undefined = undefined>(
  path: string,
  options: RequestOptions<TSchema> = {},
): Promise<Parsed<TSchema>> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': document?.documentElement?.lang || 'fr',
    ...options.headers,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';

  const needsCsrf = method !== 'GET' && !CSRF_EXEMPT.some((pattern) => pattern.test(path));
  if (needsCsrf) {
    const token = getCsrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      credentials: 'include',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiRequestError({
      code: 'NETWORK_ERROR',
      message: 'Le serveur est injoignable. Vérifiez votre connexion.',
      status: 0,
    });
  }

  const traceId = response.headers.get('X-Trace-Id') ?? undefined;

  if (response.status === 204) return undefined as Parsed<TSchema>;

  const rawText = await response.text();
  let payload: unknown = undefined;
  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    throw raiseApiError(response, payload, traceId, options.skipAuthRedirect);
  }

  if (!options.schema) return payload as Parsed<TSchema>;

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      console.error(`Réponse non conforme au contrat pour ${path}`, parsed.error.format());
    }
    throw new ApiRequestError({
      code: 'MALFORMED_RESPONSE',
      message: "La réponse du serveur ne respecte pas le contrat de l'API.",
      status: response.status,
      traceId,
    });
  }
  return parsed.data as Parsed<TSchema>;
}

/* -------------------------------------------------------------------------- */
/* Téléchargements                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Fichier prêt à être ouvert : soit une URL pré-signée rendue par l'API, soit
 * une URL d'objet locale construite à partir du flux qu'elle a servi.
 */
export interface DownloadedFile {
  url: string;
  filename: string;
  mimeType: string;
  /** Expiration de l'URL pré-signée ; `null` pour un flux local. */
  expiresAt: string | null;
  /** `true` quand `url` est un `blob:` à révoquer après usage. */
  isObjectUrl: boolean;
}

/** `attachment; filename="FAC-2026-00031.pdf"` → `FAC-2026-00031.pdf`. */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * Récupère un document servi par l'API.
 *
 * Les routes de téléchargement (`/documents/:id/download`, `/proformas/:id/pdf`,
 * `/invoices/:id/pdf`, `/credit-notes/:id/pdf`) répondent de **deux façons**
 * selon la configuration du serveur : `{ data: DownloadLink }` lorsqu'un
 * stockage objet fournit une URL pré-signée, sinon le **fichier en flux
 * direct** (`apps/api/.../billing-download.ts`). Le client teste donc le
 * `Content-Type` de la réponse au lieu de supposer du JSON.
 */
export async function apiDownload(
  path: string,
  options: { signal?: AbortSignal | undefined; fallbackFilename?: string | undefined } = {},
): Promise<DownloadedFile> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json, application/pdf, application/octet-stream',
        'Accept-Language': document?.documentElement?.lang || 'fr',
      },
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiRequestError({
      code: 'NETWORK_ERROR',
      message: 'Le serveur est injoignable. Vérifiez votre connexion.',
      status: 0,
    });
  }

  const traceId = response.headers.get('X-Trace-Id') ?? undefined;
  const contentType = response.headers.get('Content-Type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const payload = isJson ? await response.json().catch(() => undefined) : undefined;
    throw raiseApiError(response, payload, traceId);
  }

  if (isJson) {
    const parsed = DownloadLinkEnvelope.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      throw new ApiRequestError({
        code: 'MALFORMED_RESPONSE',
        message: "La réponse du serveur ne respecte pas le contrat de l'API.",
        status: response.status,
        traceId,
      });
    }
    return { ...parsed.data.data, isObjectUrl: false };
  }

  const blob = await response.blob();
  return {
    url: URL.createObjectURL(blob),
    filename:
      filenameFromDisposition(response.headers.get('Content-Disposition')) ??
      options.fallbackFilename ??
      'document.pdf',
    mimeType: contentType || blob.type || 'application/octet-stream',
    expiresAt: null,
    isObjectUrl: true,
  };
}

/**
 * Ouvre le fichier dans un nouvel onglet, puis libère l'URL d'objet lorsque
 * c'est le flux direct qui a été servi (sinon la mémoire du navigateur garde
 * le PDF jusqu'au rechargement de l'application).
 */
export function openDownloadedFile(file: DownloadedFile): void {
  window.open(file.url, '_blank', 'noopener,noreferrer');
  if (file.isObjectUrl) {
    window.setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
  }
}
